import { useContext, useState } from "react";
import { message } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { UserDataContext } from "../../../contexts/Contexts";
import { getLocalItem } from "../../../../infrastructure/storage/LocalStorage";
import { useGuidePurchase } from "../../../../infrastructure/hooks/useGuidePurchase";
import {
  getStripePublishableKey,
  isPaymentIntentClientSecret,
} from "../../../../infrastructure/utils/stripeClient";
import { formatAmount } from "../../../../shared/utils/currency";
import {
  GUIDE_CURRENCY,
  buildGuidePriceBreakdown,
} from "../../../../shared/utils/guidePricing";
import { getApiErrorMessage } from "../../../../shared/utils/apiErrorMessage";
import { isGuideBeta } from "../../../../shared/utils/guideBeta";
import GuideBetaBadge from "./GuideBetaBadge";
import GuideBetaConfirmModal from "./GuideBetaConfirmModal";
import GuidePaymentModal from "./GuidePaymentModal";

// VIVA-1214 / VIVA-1254 — panel de compra de una guía en la liga de
// contratación. Vivía como `renderPurchasePanel` dentro de `Guides2`; salió a
// su propio componente al conectarse el cobro, porque ahora tiene estado
// propio (desglose del servidor, cupón, intento de pago) que el catálogo no
// necesita conocer.
//
// EL TOTAL QUE SE MUESTRA ES EL DEL SERVIDOR (precio-preview), piso de 4.99
// USD incluido. Mientras llega —o si falla— se muestra el precio publicado del
// catálogo, pero el botón de compra no se habilita sin un total del servidor:
// nadie paga un número que el servidor no calculó.

// Quien compra: correo y nombre del usuario en sesión. Salen de
// UserDataContext (LayoutTemplate), con `userInfo` de localStorage como
// respaldo, igual que el resto de la aplicación. Son datos de contacto para el
// recibo; la identidad de la compra la toma backend del token.
const useBuyerContact = () => {
  const { userData } = useContext(UserDataContext) || {};
  const stored = getLocalItem("userInfo") || {};
  const source = userData?.email ? userData : stored;
  const fullName = [source?.nombres, source?.apellidos]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ");
  return {
    email: source?.email || "",
    fullName: fullName || source?.nickName || "",
  };
};

const couponMessageKey = (preview) => {
  if (!preview || preview.couponValid !== false) return "";
  return preview.couponReasonCode === "NOT_FOUND" ? "couponNotFound" : "couponRejected";
};

const GuidePurchasePanel = ({ card, lang, t, texts, isEnglish, onPurchased }) => {
  const buyer = useBuyerContact();
  const {
    preview,
    previewLoading,
    previewError,
    appliedCoupon,
    applyCoupon,
    removeCoupon,
    creating,
    startPurchase,
    finishOperation,
    cancelPurchase,
    waitForAccess,
  } = useGuidePurchase({ guideId: card.guideId, lang });

  const [couponCode, setCouponCode] = useState("");
  const [couponStatus, setCouponStatus] = useState("");
  const [intent, setIntent] = useState(null);
  const [purchaseError, setPurchaseError] = useState("");
  const [waiting, setWaiting] = useState(false);
  const [confirmingBeta, setConfirmingBeta] = useState(false);

  // Los mensajes del backend vienen solo en español. En la interfaz en inglés
  // se usa el texto propio; en español, el motivo real del servidor.
  const describeError = (error, fallbackKey) =>
    (!isEnglish && getApiErrorMessage(error)) || t(fallbackKey);

  // Desglose: el del servidor si llegó; si no, el precio publicado del catálogo.
  const desglose = preview
    ? { ...preview, hasDiscount: preview.betaDiscount > 0 || preview.couponDiscount > 0, currencyMismatch: false }
    : buildGuidePriceBreakdown({ basePrice: card.price, currencyCode: card.currencyCode });

  const importe = (valor) => formatAmount(valor, desglose.currencyCode, lang);
  const couponInputId = `g2-coupon-${card.guideId}`;
  const canBuy = Boolean(preview) && !previewLoading && !creating && !waiting;

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponStatus(t("couponEmpty"));
      return;
    }
    setCouponStatus("");
    const { preview: result, error } = await applyCoupon(couponCode);
    if (error) {
      // El cupón no se pudo validar (fallo del servicio, no un rechazo): el
      // precio sin cupón sigue en pantalla y se puede comprar con él.
      setCouponStatus(t("couponCheckError"));
    } else if (result?.couponValid === true) {
      setCouponStatus(t("couponApplied", result.couponCode || couponCode.trim()));
    } else if (result) {
      setCouponStatus(t(couponMessageKey(result) || "couponRejected"));
    }
  };

  const handleRemoveCoupon = async () => {
    setCouponCode("");
    setCouponStatus("");
    await removeCoupon();
  };

  // VIVA-1165: una guía Beta pasa primero por el disclaimer; el cobro arranca
  // solo al pulsar «Continuar al pago». Una guía sin Beta va directo.
  const handleBuyClick = () => {
    setPurchaseError("");
    if (isGuideBeta(card)) {
      setConfirmingBeta(true);
      return;
    }
    startPaymentFlow();
  };

  const handleBetaContinue = () => {
    setConfirmingBeta(false);
    startPaymentFlow();
  };

  const startPaymentFlow = async () => {
    setPurchaseError("");

    // Sin llave publicable de Stripe no se puede mostrar el formulario. Se
    // corta ANTES de crear el intento, para no dejar un PaymentIntent huérfano.
    if (!getStripePublishableKey()) {
      console.warn("Falta REACT_APP_STRIPE_PUBLISHABLE_KEY: no se puede cobrar la guía.");
      setPurchaseError(t("paymentUnavailable"));
      return;
    }

    try {
      const created = await startPurchase(buyer);
      if (!isPaymentIntentClientSecret(created?.clientSecret)) {
        console.error("create-payment-intent-generico no devolvió un clientSecret válido.");
        await cancelPurchase(created || {});
        setPurchaseError(t("paymentStartError"));
        return;
      }
      setIntent(created);
    } catch (error) {
      setPurchaseError(
        error?.response?.status === 409 &&
          /monto/i.test(getApiErrorMessage(error))
          ? t("priceChanged")
          : describeError(error, "paymentStartError")
      );
    }
  };

  // CA-11: cerrar el formulario sin pagar cancela el intento.
  const handleCancelPayment = async () => {
    const current = intent;
    setIntent(null);
    await cancelPurchase(current || {});
  };

  // CA-09: el navegador no habilita nada. Se espera a que el webhook registre
  // la compra y entonces se refresca el catálogo para que la tarjeta cambie a
  // "Leer versión completa".
  //
  // La espera va con el `paymentIntentId` para que pueda preguntarle al propio
  // intento si el webhook ya corrió, en vez de deducirlo solo de que la guía
  // aparezca en mis-guias-compradas.
  //
  // El resultado se le pasa a la pantalla: si el webhook todavía no corrió, ESTE
  // panel no puede seguir esperando —en cuanto la tarjeta deja de ofrecer la
  // compra, o el comprador cierra el precio, se desmonta— así que quien sigue
  // pendiente es la pantalla, que vive mientras dure la visita.
  const handleSucceeded = async () => {
    const current = intent;
    setIntent(null);
    finishOperation(current?.operationId);
    setWaiting(true);
    const hasAccess = await waitForAccess({ paymentIntentId: current?.paymentIntentId });
    setWaiting(false);
    if (hasAccess) {
      message.success(t("paymentSuccess"));
    } else {
      message.info(t("paymentPendingAccess"), 6);
    }
    onPurchased?.(card, {
      paymentIntentId: current?.paymentIntentId,
      granted: hasAccess,
    });
  };

  const handleProcessing = () => {
    const current = intent;
    setIntent(null);
    finishOperation(current?.operationId);
    message.info(t("paymentProcessing"), 6);
    onPurchased?.(card, {
      paymentIntentId: current?.paymentIntentId,
      granted: false,
    });
  };

  // Pinta en rojo todo lo que no sea "cupón aplicado".
  const couponIsError = Boolean(couponStatus) && !appliedCoupon;

  return (
    <div className="g2-purchase" onClick={(e) => e.stopPropagation()} role="presentation">
      <div className="g2-purchase-row">
        <span className="g2-purchase-label">{t("priceBase")}</span>
        <span className="g2-purchase-amount">{importe(desglose.basePrice)}</span>
      </div>

      {desglose.betaDiscount > 0 ? (
        <div className="g2-purchase-row">
          <span className="g2-purchase-label">{t("betaDiscountRow")}</span>
          <span className="g2-purchase-discount">-{importe(desglose.betaDiscount)}</span>
        </div>
      ) : null}

      {desglose.couponDiscount > 0 ? (
        <div className="g2-purchase-row">
          <span className="g2-purchase-label">{t("couponRow")}</span>
          <span className="g2-purchase-discount">-{importe(desglose.couponDiscount)}</span>
        </div>
      ) : null}

      {/* Sin descuentos el total es el precio base: repetirlo solo ensucia. */}
      {desglose.hasDiscount || desglose.floorApplied ? (
        <div className="g2-purchase-row g2-purchase-row--total">
          <span className="g2-purchase-label">{t("totalRow")}</span>
          <span className="g2-purchase-amount">{importe(desglose.total)}</span>
        </div>
      ) : null}

      {desglose.floorApplied ? <p className="g2-purchase-note">{t("floorNote")}</p> : null}

      {/* CA-08. Con el desglose del servidor la moneda es siempre USD. El aviso
          de moneda distinta solo aplica al respaldo del catálogo. */}
      {desglose.currencyMismatch ? (
        <p className="g2-purchase-note g2-purchase-note--mismatch">
          {t("currencyMismatchNote", desglose.currencyCode, GUIDE_CURRENCY)}
        </p>
      ) : (
        <p className="g2-purchase-note">{t("chargedIn", desglose.currencyCode)}</p>
      )}

      {/* El indicador va como icono y no como `Spin`: `Spin` pinta un <div> y
          aqui esta dentro de un parrafo, lo que es HTML invalido (React lo
          reporta como validateDOMNesting). El icono es un <span>. */}
      {previewLoading ? (
        <p className="g2-purchase-note" role="status">
          <LoadingOutlined spin /> {t("previewLoading")}
        </p>
      ) : null}

      {previewError && !previewLoading ? (
        <p className="g2-purchase-note g2-purchase-note--error" role="alert">
          {describeError(previewError, "previewError")}
        </p>
      ) : null}

      <div className="g2-coupon">
        <label className="g2-purchase-label" htmlFor={couponInputId}>
          {t("couponQuestion")}
        </label>
        <div className="g2-coupon-row">
          <input
            id={couponInputId}
            type="text"
            className="g2-coupon-input"
            value={couponCode}
            placeholder={t("couponPlaceholder")}
            disabled={Boolean(appliedCoupon)}
            onChange={(e) => {
              setCouponCode(e.target.value);
              setCouponStatus("");
            }}
          />
          {appliedCoupon ? (
            <button type="button" className="g2-coupon-btn" onClick={handleRemoveCoupon}>
              {t("couponRemove")}
            </button>
          ) : (
            <button
              type="button"
              className="g2-coupon-btn"
              onClick={handleApplyCoupon}
              disabled={previewLoading}
            >
              {t("couponApply")}
            </button>
          )}
        </div>
        {couponStatus ? (
          <p
            className={`g2-purchase-note ${couponIsError ? "g2-purchase-note--error" : ""}`}
            role="status"
          >
            {couponStatus}
          </p>
        ) : null}
      </div>

      {/* VIVA-1246: distintivo de Beta ARRIBA del botón que cobra. El
          disclaimer del checkout (VIVA-1165) ya no va aquí: es el paso
          GuideBetaConfirmModal, que se abre al pulsar «Comprar guía» y es lo
          único que da paso al pago. */}
      <GuideBetaBadge guide={card} texts={texts} isEnglish={isEnglish} />

      {purchaseError ? (
        <p className="g2-purchase-note g2-purchase-note--error" role="alert">
          {purchaseError}
        </p>
      ) : null}

      {waiting ? (
        <p className="g2-purchase-note" role="status">{t("waitingAccess")}</p>
      ) : null}

      <button
        type="button"
        className="g2-purchase-btn"
        onClick={handleBuyClick}
        disabled={!canBuy}
      >
        {t("buyGuide")}
      </button>

      <GuideBetaConfirmModal
        open={confirmingBeta}
        guide={card}
        texts={texts}
        isEnglish={isEnglish}
        t={t}
        onContinue={handleBetaContinue}
        onCancel={() => setConfirmingBeta(false)}
      />

      <GuidePaymentModal
        open={Boolean(intent)}
        clientSecret={intent?.clientSecret}
        lang={lang}
        t={t}
        email={buyer.email}
        amountLabel={preview ? importe(preview.total) : ""}
        onSucceeded={handleSucceeded}
        onProcessing={handleProcessing}
        onCancel={handleCancelPayment}
      />
    </div>
  );
};

export default GuidePurchasePanel;
